import { IUser, UserType } from 'common/interfaces/user';
import { LeadsSearchFilter } from 'stores/Leads';

export enum EDashboardView {
    Grid = 'grid',
    List = 'list',
}

export interface IProviderFindApiQuery {
    $limit: number;
    $skip: number;
    search?: string;
    searchType?: LeadsSearchFilter;
    lastCompletedActivityTypeId?: number;
    fromDate?: string;
    toDate?: string;
    noActivity?: boolean;
}

export interface IProviderNotesApiQuery {
    $limit: number;
    $skip: number;
    search?: string;
}

export interface IProviderInfo {
    total: number;
    limit: number;
    skip: number;
    data: IProvider[];
}

export interface IProviderActivity {
    id: number;
    note: string;
    activityTypeId: number;
    activityTypeName: string;
    createdAt: string;
    updatedAt: string;
}

export enum CreateNewLeadStatus {
    None,
    Saving,
    ProviderIdRequired,
    LegalBusNameRequired,
    ServerSideError,
    Success,
}

export interface IProvidersToDisplay {
    totalLeads: number;
    leadsIds: number[];
    isLoading: boolean;
    firstFetch: boolean;
    lastFetchTimestamp: number;
}

export interface INotesToDisplay {
    totalNotes: number;
    notesIds: number[];
    isLoading: boolean;
    firstFetch: boolean;
    lastFetchTimestamp: number;
}

export interface IProvider {
    databaseId: number;
    providerId: string;
    groupId: string;
    legalBusName: string;
    dba: string;
    lastCompletedActivity: IProviderActivity | null;
    plCity: string;
    plState: string;
    plZipcode: number;
    plPhone: string;
}

export interface IProviderShort {
    databaseId: number;
    providerId: string;
    legalBusName: string;
    dba: string;
}

export interface IProviderShortWithSlpcode extends IProviderShort {
    slpcode: number | null;
}

export interface IBusinessType {
    id: number;
    name: string;
}

export interface IDrugLicense {
    id: number;
    name: string;
}

export interface ISalesRep {
    id: number;
    username: string;
    name: string | null;
    email: string | null;
    slpcode: number | null;
    primary: 1 | 0;
    status: boolean;
}

export interface IProviderRelation {
    id: string;
    typeId: string;
    relationType: {
        typeId: string;
        description: string;
    };
    name: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zipcode: number | null;
    phone: number | null;
    extension: number | null;
    fax: number | null;
    contactName: string | null;
    contactTitle: string | null;
    contactEmail: string | null;
    parentOrganizationId: number | null;
}

export interface IProviderRelationMinimal {
    name: string;
    description: string;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zipcode: string | null;
    phone: number | null;
}

export interface IProviderActivitiesAssignedToUser {
    id: number;
    username: string;
    name: string | null;
    type: UserType;
}

export interface IProviderDetails {
    databaseId: number;
    providerId: string;
    providerGroup: IProviderGroup;
    active: boolean;
    salesReps: ISalesRep[];
    dba: string | null;
    legalBusName: string | null;
    createdManually: boolean | null;
    type: string | null;
    dispenserClass: string | null;
    npiNumber: string | null;
    relationId: string | null;
    relationType: number | null;
    relations: IProviderRelationMinimal[];
    activitiesAssignedToUsers: IProviderActivitiesAssignedToUser[];
    typeOfBusiness: number | null;
    typeOfBusinessData: {
        id: number;
        name: string;
    } | null;
    typeOfBusinessOthers: string | null;
    drugLicenseType: number | null;
    typeOfDrugLicenseData: {
        id: number;
        name: string;
    } | null;
    drugLicenseTypeOthers: string | null;
    license: string | null;
    licenseExpDate: string | null;
    plAddress1: string | null;
    plAddress2: string | null;
    plCity: string | null;
    plState: string | null;
    plZipcode: number | null;
    plEmail: string | null;
    plPhone: string | null;
    plExt: string | null;
    plFax: string | null;
    notes?: ILeadNote[];
    cardcode: string | null;
    customerCreated: boolean | null;
}

export interface IProviderGroup {
    id: number;
    name: string;
}

export interface IEditedProviderDetails {
    dba: string;
    legalBusName: string;
    providerId: string;
    salesReps: ISalesRep[];
    providerTypeCode: string | null;
    providerTypeName: string | null;
    dispenserClassCode: string | null;
    dispenserClassName: string | null;
    typeOfBusinessId: number;
    typeOfBusinessName: string;
    drugLicenseTypeId: number;
    drugLicenseTypeName: string;
    plAddress1: string;
    plAddress2: string;
    plCity: string;
    plState: string;
    plZipcode: string;
    plPhone: string;
    plExt: string;
    plFax: string;
    plEmail: string;
    modified: boolean;
}

export interface ILeadsDbInfo extends ILeadsInfo {
    id: ILeadsInfoItem;
}

export interface ILeadsInfo {
    legalBusName: ILeadsInfoItem;
    dba: ILeadsInfoItem;
    plAddress1: ILeadsInfo;
    plAddress2: ILeadsInfo;
    plCity: ILeadsInfoItem;
    plState: ILeadsInfoItem;
    plZipcode: ILeadsInfoItem;
}

export interface ILeadsInfoItem {
    name?: string;
    label?: string;
    placeholder?: string;
}

export interface ILeadNotesSearchInfo {
    total: number;
    limit: number;
    skip: number;
    data: ILeadNoteSearched[];
}

export interface ILeadNotesInfo {
    total: number;
    limit: number;
    skip: number;
    data: ILeadNote[];
}

export interface ILeadNoteUser {
    id: number;
    username: string;
    name: string | null;
    email: string | null;
}

export interface ILeadNote {
    id: number;
    comment: string;
    hidden: number | null;
    providerId: number;
    createdAt: string | null;
    createdByUser: ILeadNoteUser;
    updatedAt: string | null;
    updatedByUser: ILeadNoteUser;
}

export interface ILeadNoteWithIndex extends ILeadNote {
    index: number;
}

export interface ILeadNoteSearched {
    id: number;
    providerDatabaseId: number;
    providerId: string;
    userId: number;
    username: string;
    name: string | null;
    slpcode: number;
    comment: string;
    dba: string;
    legalBusName: string;
    updatedAt: string;
}

export interface ILeadNewNote {
    isSaving: boolean;
    error: boolean;
    errorAllFieldsRequired: boolean;
    html: string;
    text: string;
}

export interface ILeadEditNote {
    id: number | null;
    isSaving: boolean;
    error: boolean;
    html: string;
    text: string;
}

export interface ILeadNoteVisibility {
    id: number | null;
    isSaving: boolean;
    error: boolean;
}

export interface ILeadNotesSubmitInfo {
    comment: string;
    providerId: number;
}

export enum EVisibility {
    Visible = 0,
    Hidden = 1,
}

export interface ILeadNotesUpdateInfo {
    comment?: string;
    providerId?: number;
    hidden?: EVisibility;
}

export interface ILeadNoteDisplayInfo {
    note: string;
    authorName: string;
    editDate: string;
}

export enum LeadGroupModalStatus {
    Closed,
    OpenedOnGroupList,
    OpenedOnSpecificGroup,
    OpenedOnCreateGroup,
}

export enum CreateLeadModalStatus {
    Closed,
    Opened,
}

export interface ICreateNewLead {
    status: CreateNewLeadStatus;
    providerId: null | string;
    legalBusName: null | string;
    dba: null | string;
}

export interface ILeadGroupInfo {
    total: number;
    limit: number;
    skip: number;
    data: ILeadGroup[];
}

export interface ILeadGroup {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
    createdByUserId: number;
    createdByUser: IUser | null;
    provider: IProviderShortWithSlpcode[];
}

export interface ILeadGroupDropdown {
    value: string;
    label: string;
}

export interface IProviderTypeOrClass {
    code: string;
    name: string;
}

export interface IProviderTypesOrClassesResponse {
    total: number;
    skip: number;
    limit: number;
    data: IProviderTypeOrClass[];
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/common/interfaces/provider.ts